import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import Seo from "../components/Seo";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { BarChart3, Search, Users, Eye, UserPlus, TrendingUp, Smartphone, Monitor, MousePointerClick } from "lucide-react";

const GREEN = "#10B981";
const BLUE = "#3B82F6";
const AMBER = "#F59E0B";

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card className="p-5 bg-[#121214] border-white/10" data-testid={testid}>
      <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-[0.15em]">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <div className="mt-3 text-3xl font-black tracking-tight text-zinc-50">{value}</div>
      {sub && <div className="mt-1 text-sm text-zinc-500">{sub}</div>}
    </Card>
  );
}

function Panel({ title, children, testid }) {
  return (
    <Card className="p-5 bg-[#121214] border-white/10" data-testid={testid}>
      <h3 className="text-lg font-semibold text-zinc-100 mb-4">{title}</h3>
      {children}
    </Card>
  );
}

const nf = (n) => new Intl.NumberFormat("fr-FR").format(n ?? 0);
const tooltipStyle = { background: "#0A0A0A", border: "1px solid #27272A", borderRadius: 8, color: "#FAFAFA" };

function AudienceTab({ period }) {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-analytics", period],
    queryFn: async () => (await api.get("/admin/analytics", { params: { period } })).data,
  });
  if (isLoading || !data) return <div className="text-zinc-500 py-12 text-center">Chargement…</div>;
  const funnel = data.funnel || {};
  const funnelMax = Math.max(funnel.visitors || 1, 1);
  const funnelRows = [
    { label: "Visiteurs uniques", value: funnel.visitors || 0, color: GREEN },
    { label: "Inscriptions", value: funnel.registrations || 0, color: BLUE },
    { label: "Abonnés payants", value: funnel.paying || 0, color: AMBER },
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Visiteurs uniques" value={nf(data.unique_visitors)} testid="stat-visitors" />
        <StatCard icon={Eye} label="Pages vues" value={nf(data.total_pageviews)} testid="stat-pageviews" />
        <StatCard icon={UserPlus} label="Inscriptions" value={nf(data.registrations)} testid="stat-registrations" />
        <StatCard icon={TrendingUp} label="Taux de conversion" value={`${data.conversion_rate}%`} sub={`${nf(data.paying)} abonnés payants`} testid="stat-conversion" />
      </div>

      <Panel title="Visiteurs & pages vues par jour" testid="chart-by-day">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data.by_day}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
            <XAxis dataKey="date" stroke="#71717A" fontSize={11} />
            <YAxis stroke="#71717A" fontSize={11} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            <Line type="monotone" dataKey="visitors" name="Visiteurs" stroke={GREEN} strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="pageviews" name="Pages vues" stroke={BLUE} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Pages populaires" testid="table-top-pages">
          <div className="space-y-2">
            {(data.top_pages || []).map((p, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-white/5 py-2">
                <span className="text-zinc-300 truncate mr-3">{p.path}</span>
                <span className="text-zinc-100 font-semibold shrink-0">{nf(p.views)}</span>
              </div>
            ))}
            {!(data.top_pages || []).length && <div className="text-zinc-500 text-sm">Aucune donnée</div>}
          </div>
        </Panel>
        <Panel title="Sources de trafic" testid="table-sources">
          <div className="space-y-2">
            {(data.sources || []).map((s, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-white/5 py-2">
                <span className="text-zinc-300 truncate mr-3">{s.source}</span>
                <span className="text-zinc-100 font-semibold shrink-0">{nf(s.visits)}</span>
              </div>
            ))}
            {!(data.sources || []).length && <div className="text-zinc-500 text-sm">Aucune donnée</div>}
          </div>
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Mobile / Desktop" testid="chart-devices">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={data.devices} dataKey="count" nameKey="device" cx="50%" cy="50%" outerRadius={85} label>
                {(data.devices || []).map((d, i) => (
                  <Cell key={i} fill={d.device === "mobile" ? GREEN : BLUE} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-6 text-sm text-zinc-400">
            <span className="flex items-center gap-1"><Smartphone className="w-4 h-4" /> Mobile</span>
            <span className="flex items-center gap-1"><Monitor className="w-4 h-4" /> Desktop</span>
          </div>
        </Panel>
        <Panel title="Clics les plus fréquents" testid="table-clicks">
          <div className="space-y-2">
            {(data.top_clicks || []).map((c, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-white/5 py-2">
                <span className="text-zinc-300 truncate mr-3 flex items-center gap-2"><MousePointerClick className="w-3.5 h-3.5 text-zinc-500" />{c.name}</span>
                <span className="text-zinc-100 font-semibold shrink-0">{nf(c.count)}</span>
              </div>
            ))}
            {!(data.top_clicks || []).length && <div className="text-zinc-500 text-sm">Aucun clic enregistré</div>}
          </div>
        </Panel>
      </div>

      <Panel title="Entonnoir de conversion" testid="funnel">
        <div className="space-y-3">
          {funnelRows.map((r, i) => (
            <div key={i}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-zinc-300">{r.label}</span>
                <span className="text-zinc-100 font-semibold">{nf(r.value)}</span>
              </div>
              <div className="h-3 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${Math.max((r.value / funnelMax) * 100, 2)}%`, background: r.color }} />
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

function Delta({ value, invert }) {
  if (value === 0 || value === undefined) return <span className="text-zinc-500 text-xs">±0</span>;
  const good = invert ? value < 0 : value > 0;
  return (
    <span className={`text-xs font-medium ${good ? "text-emerald-400" : "text-red-400"}`}>
      {value > 0 ? "+" : ""}{value}
    </span>
  );
}

function SeoTab({ period }) {
  const { data, isLoading } = useQuery({
    queryKey: ["admin-seo", period],
    queryFn: async () => (await api.get("/admin/seo", { params: { period } })).data,
  });
  if (isLoading || !data) return <div className="text-zinc-500 py-12 text-center">Chargement…</div>;
  if (!data.configured) {
    return (
      <Card className="p-10 bg-[#121214] border-white/10 text-center" data-testid="seo-not-connected">
        <Search className="w-10 h-10 mx-auto text-zinc-600 mb-3" />
        <div className="text-zinc-200 text-lg font-semibold">Search Console pas encore connectée</div>
        <div className="text-zinc-500 text-sm mt-2 max-w-md mx-auto">
          Ajoutez la clé du compte de service Google (GSC_SERVICE_ACCOUNT_JSON) pour afficher ici vos clics,
          impressions et mots-clés Google.
        </div>
      </Card>
    );
  }
  if (!data.has_data) {
    return (
      <Card className="p-10 bg-[#121214] border-white/10 text-center" data-testid="seo-no-data">
        <Search className="w-10 h-10 mx-auto text-zinc-600 mb-3" />
        <div className="text-zinc-200 text-lg font-semibold">Aucune donnée Search Console pour l'instant</div>
        <div className="text-zinc-500 text-sm mt-2">Les données arrivent sous 1 à 3 jours après la première synchronisation.</div>
      </Card>
    );
  }
  const s = data.summary, d = data.delta;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={MousePointerClick} label="Clics Google" value={nf(s.clicks)} sub={<Delta value={d.clicks} />} testid="seo-clicks" />
        <StatCard icon={Eye} label="Impressions" value={nf(s.impressions)} sub={<Delta value={d.impressions} />} testid="seo-impressions" />
        <StatCard icon={TrendingUp} label="Taux de clic moyen" value={`${s.ctr}%`} sub={<Delta value={d.ctr} />} testid="seo-ctr" />
        <StatCard icon={BarChart3} label="Position moyenne" value={s.position} sub={<Delta value={d.position} invert />} testid="seo-position" />
      </div>

      <Panel title="Clics & impressions par jour" testid="seo-chart">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data.by_day}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
            <XAxis dataKey="date" stroke="#71717A" fontSize={11} />
            <YAxis stroke="#71717A" fontSize={11} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend />
            <Line type="monotone" dataKey="clicks" name="Clics" stroke={GREEN} strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="impressions" name="Impressions" stroke={BLUE} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Mots-clés" testid="seo-queries">
          <SeoTable rows={data.queries} col="query" label="Mot-clé" />
        </Panel>
        <Panel title="Pages" testid="seo-pages">
          <SeoTable rows={data.pages} col="page" label="Page" />
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="Presque en 1re page (positions 8–20)" testid="seo-almost">
          <SeoTable rows={data.almost_first_page} col="query" label="Mot-clé" />
        </Panel>
        <Panel title="Titres à retravailler (CTR < 2%)" testid="seo-low-ctr">
          <SeoTable rows={data.titles_to_improve} col="query" label="Mot-clé" />
        </Panel>
      </div>
    </div>
  );
}

function SeoTable({ rows, col, label }) {
  if (!rows || !rows.length) return <div className="text-zinc-500 text-sm">Aucune donnée</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-zinc-500 text-xs uppercase tracking-wider border-b border-white/10">
            <th className="text-left py-2 font-medium">{label}</th>
            <th className="text-right py-2 font-medium">Clics</th>
            <th className="text-right py-2 font-medium">Impr.</th>
            <th className="text-right py-2 font-medium">CTR</th>
            <th className="text-right py-2 font-medium">Pos.</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 20).map((r, i) => (
            <tr key={i} className="border-b border-white/5">
              <td className="py-2 text-zinc-300 truncate max-w-[180px]">{r[col]}</td>
              <td className="py-2 text-right text-zinc-100">{nf(r.clicks)}</td>
              <td className="py-2 text-right text-zinc-400">{nf(r.impressions)}</td>
              <td className="py-2 text-right text-zinc-400">{r.ctr}%</td>
              <td className="py-2 text-right text-zinc-400">{r.position}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminStatsPage() {
  const [tab, setTab] = useState("audience");
  const [period, setPeriod] = useState("30d");
  const [seoPeriod, setSeoPeriod] = useState("28d");
  return (
    <div className="min-h-screen" data-testid="admin-stats-page">
      <Seo title="Statistiques" path="/admin/statistiques" noindex />
      <section className="border-b border-white/5">
        <div className="mx-auto max-w-7xl px-6 py-12">
          <div className="flex items-center gap-3 text-emerald-400 text-xs uppercase tracking-[0.2em] mb-2">
            <BarChart3 className="w-4 h-4" /> Admin
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-zinc-50">Statistiques</h1>
          <p className="text-zinc-400 mt-2">Audience anonyme (sans cookie) et performances de référencement Google.</p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 py-8">
        <Tabs value={tab} onValueChange={setTab}>
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <TabsList className="bg-[#121214] border border-white/10">
              <TabsTrigger value="audience" data-testid="tab-audience">Audience</TabsTrigger>
              <TabsTrigger value="seo" data-testid="tab-seo">Référencement</TabsTrigger>
            </TabsList>
            {tab === "audience" ? (
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-40 bg-[#121214] border-white/10" data-testid="period-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">7 derniers jours</SelectItem>
                  <SelectItem value="30d">30 derniers jours</SelectItem>
                  <SelectItem value="90d">90 derniers jours</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Select value={seoPeriod} onValueChange={setSeoPeriod}>
                <SelectTrigger className="w-40 bg-[#121214] border-white/10" data-testid="seo-period-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="28d">28 derniers jours</SelectItem>
                  <SelectItem value="90d">90 derniers jours</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          <TabsContent value="audience"><AudienceTab period={period} /></TabsContent>
          <TabsContent value="seo"><SeoTab period={seoPeriod} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
